<template>
  <div class="closure-section">

    <!-- Toggle cierre -->
    <div class="closure-toggle-banner" :class="{ active: useAdvancedClosureByType[selectedType] }">
      <div class="toggle-info">
        <v-icon size="16" :color="useAdvancedClosureByType[selectedType] ? '#2D4B95' : '#8896b3'" class="mr-2">
          {{ useAdvancedClosureByType[selectedType] ? 'mdi-shield-check-outline' : 'mdi-shield-off-outline' }}
        </v-icon>
        <div>
          <div class="toggle-title">
            {{ useAdvancedClosureByType[selectedType] ? 'Sistema de cierre por perfiles' : 'Cierre tradicional' }}
          </div>
          <div class="toggle-desc">
            {{ useAdvancedClosureByType[selectedType]
              ? 'Los perfiles seleccionados deben aprobar el cierre.'
              : 'Se usa el sistema de cierre original de la plataforma.' }}
          </div>
        </div>
      </div>
      <button
        class="toggle-switch"
        :class="{ on: useAdvancedClosureByType[selectedType] }"
        @click="useAdvancedClosureByType[selectedType] = !useAdvancedClosureByType[selectedType]"
        type="button"
      >
        <span class="toggle-knob" />
      </button>
    </div>

    <!-- Profiles section -->
    <transition name="fade-slide">
      <div v-if="useAdvancedClosureByType[selectedType]" class="profiles-section">
        <div class="section-label">
          <v-icon size="13" color="#2D4B95" class="mr-1">mdi-account-group-outline</v-icon>
          {{ selectedType === 'agps' ? 'Perfiles permitidos para edición' : 'Perfiles requeridos para cierre' }}
        </div>
        <p class="section-desc">
          <span v-if="selectedType === 'agps'">
            Selecciona qué perfiles pueden permitir la edición de
            <strong>{{ itemTypeLabel.toLowerCase() }}</strong>.
          </span>
          <span v-else-if="selectedType === 'adicional'">
            Selecciona qué perfiles deben aprobar el
            <strong>{{ itemTypeLabel.toLowerCase() }}</strong>.
          </span>
          <span v-else>
            Selecciona qué perfiles deben aprobar el cierre de una
            <strong>{{ itemTypeLabel.toLowerCase() }}</strong>.
          </span>
          Todos los perfiles marcados deben dar su visto bueno.
        </p>

        <div class="profiles-grid">
          <div
            v-for="profile in availableProfiles"
            :key="profile.id"
            class="profile-card"
            :class="{ selected: isSelected(profile.id) }"
            @click="toggleProfile(profile.id)"
          >
            <div class="profile-avatar" :style="avatarStyle(profile, isSelected(profile.id))">
              <span class="avatar-initials">{{ initials(profile.name) }}</span>
              <div class="check-badge" v-if="isSelected(profile.id)">
                <v-icon size="10" color="white">mdi-check</v-icon>
              </div>
            </div>
            <div class="profile-name">{{ profile.name }}</div>
            <div class="profile-desc">{{ profile.description }}</div>
          </div>
        </div>

        <transition name="fade-slide">
          <div v-if="selectedProfiles.length > 0" class="selected-summary">
            <div class="summary-chips">
              <div v-for="id in selectedProfiles" :key="id" class="summary-chip">
                <span>{{ profileName(id) }}</span>
                <button class="chip-remove" @click.stop="toggleProfile(id)">
                  <v-icon size="10" color="#2D4B95">mdi-close</v-icon>
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>

  </div>
</template>

<script>
import { setClosureConfig } from '@/helpers/api/project'

export default {
  name: 'ClosureConfigSection',

  props: {
    project:      { type: Object, required: true },
    selectedType: { type: String, required: true }
  },

  data() {
    return {
      useAdvancedClosureByType: { rdi: false, tarea: false, adicional: false, agps: false },
      selectedProfiles: [],
      profilesByType:   { rdi: [], tarea: [], adicional: [], agps: [], agps_approval: [] },

      itemTypeMap: { 1: 'rdi', 2: 'tarea', 3: 'adicional', 4: 'agps', 5: 'agps_approval' },

      avatarColors: [
        '#2D4B95', '#3D6BD4', '#E67E22', '#27AE60',
        '#8E44AD', '#E74C3C', '#16A085', '#D35400'
      ]
    }
  },

  computed: {
    availableProfiles() {
      const creator     = { id: -1, name: 'Creador',     description: 'Creador del ítem' }
      const responsable = { id: -2, name: 'Responsable', description: 'Responsable del ítem' }
      const projectProfiles = this.$store.projectsStore.state.projectProfiles || []
      let base = this.selectedType === 'agps'
        ? [...projectProfiles]
        : [creator, ...projectProfiles]
      if (this.selectedType === 'tarea') {
        base = [responsable, ...base.filter(p => p.id !== -2)]
      }
      return base
    },

    closureConfig() {
      return this.project.closure_config || []
    },

    itemTypeLabel() {
      const map = { rdi: 'RDI', tarea: 'Tarea', adicional: 'Adicional', agps: 'Estados de Pago', agps_approval: 'Aprobación de Estados de Pago' }
      return map[this.selectedType] || this.selectedType
    },

    isValid() {
      if (this.useAdvancedClosureByType[this.selectedType] && this.selectedProfiles.length === 0) return false
      return true
    },

    payload() {
      const typeToId = { rdi: 1, tarea: 2, adicional: 3, agps: 4, agps_approval: 5 }
      const base = {
        item_type:             typeToId[this.selectedType],
        project_id:            this.project.id,
        use_advanced_closure:  this.useAdvancedClosureByType[this.selectedType]
      }
      if (this.useAdvancedClosureByType[this.selectedType]) {
        base.advanced_closure_profiles = [...this.selectedProfiles]
      }
      return base
    }
  },

  watch: {
    selectedType(val) {
      this.selectedProfiles = this.profilesByType[val] || []
    }
  },

  methods: {
    loadConfiguration() {
      this.useAdvancedClosureByType = { rdi: false, tarea: false, adicional: false, agps: false, agps_approval: false }
      this.profilesByType           = { rdi: [],    tarea: [],    adicional: [],    agps: [],    agps_approval: [] }
      this.selectedProfiles         = []

      if (this.closureConfig && this.closureConfig.length > 0) {
        this.closureConfig.forEach(config => {
          const typeKey = this.itemTypeMap[config.item_type]
          if (typeKey) {
            this.useAdvancedClosureByType[typeKey] = config.use_advanced_closure
            if (config.use_advanced_closure && config.advanced_closure_profiles) {
              this.profilesByType[typeKey] = [...config.advanced_closure_profiles]
            }
          }
        })
      }
      this.selectedProfiles = this.profilesByType[this.selectedType] || []
    },

    isSelected(id)    { return this.selectedProfiles.includes(id) },

    toggleProfile(id) {
      const idx = this.selectedProfiles.indexOf(id)
      if (idx === -1) this.selectedProfiles.push(id)
      else            this.selectedProfiles.splice(idx, 1)
      this.profilesByType[this.selectedType] = [...this.selectedProfiles]
    },

    profileName(id) {
      const p = this.availableProfiles.find(p => p.id === id)
      return p ? p.name : id
    },

    initials(name) {
      return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    },

    avatarStyle(profile, selected) {
      const color = this.avatarColors[this.availableProfiles.indexOf(profile) % this.avatarColors.length]
      return selected
        ? { background: color, boxShadow: `0 0 0 3px ${color}40` }
        : { background: `${color}18`, border: `2px solid ${color}40` }
    },

    async save() {
      await setClosureConfig(this.payload)
    }
  }
}
</script>

<style scoped>
* { box-sizing: border-box; }

.closure-toggle-banner {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px; border-radius: 12px;
  border: 1.5px solid #e2e8f4; background: #f7f9fc;
  margin-bottom: 20px; transition: all 0.25s ease;
}
.closure-toggle-banner.active { border-color: #2D4B9550; background: #f0f4ff; }
.toggle-info { display: flex; align-items: center; gap: 2px; }
.toggle-title { font-size: 13px; font-weight: 600; color: #2d3748; line-height: 1.3; }
.toggle-desc  { font-size: 11.5px; color: #8896b3; margin-top: 2px; }
.toggle-switch {
  flex-shrink: 0; width: 42px; height: 24px; border-radius: 12px;
  border: none; background: #d1d9e8; cursor: pointer;
  position: relative; transition: background 0.22s ease; padding: 0;
}
.toggle-switch.on { background: #2D4B95; }
.toggle-knob {
  position: absolute; top: 3px; left: 3px;
  width: 18px; height: 18px; border-radius: 50%;
  background: white; box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  transition: transform 0.22s cubic-bezier(.34,1.56,.64,1); display: block;
}
.toggle-switch.on .toggle-knob { transform: translateX(18px); }

.section-label {
  font-size: 11px; font-weight: 600; color: #2D4B95;
  text-transform: uppercase; letter-spacing: 0.08em;
  display: flex; align-items: center; margin-bottom: 6px;
}
.section-desc { font-size: 12.5px; color: #8896b3; margin: 0 0 16px; line-height: 1.55; }
.section-desc strong { color: #4a5568; font-weight: 600; }

.profiles-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 16px;
}
.profile-card {
  display: flex; flex-direction: column; align-items: center;
  padding: 14px 10px 12px; border-radius: 12px;
  border: 1.5px solid #e8edf5; cursor: pointer;
  transition: all 0.2s ease; background: #fafbfd; user-select: none;
}
.profile-card:hover { border-color: #2D4B9560; background: #f0f4ff; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(45,75,149,0.1); }
.profile-card.selected { border-color: #2D4B95; background: #f0f4ff; box-shadow: 0 4px 16px rgba(45,75,149,0.15); }
.profile-avatar {
  width: 44px; height: 44px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  position: relative; margin-bottom: 8px; transition: all 0.2s ease;
}
.avatar-initials { font-size: 13px; font-weight: 700; }
.profile-card:not(.selected) .avatar-initials { color: #2D4B95; }
.profile-card.selected .avatar-initials { color: #ffffff; }
.check-badge {
  position: absolute; bottom: -2px; right: -2px;
  width: 18px; height: 18px; border-radius: 50%;
  background: #27AE60; display: flex; align-items: center; justify-content: center;
  border: 2px solid white;
}
.profile-name { font-size: 12px; font-weight: 600; color: #2d3748; text-align: center; line-height: 1.2; }
.profile-desc { font-size: 10.5px; color: #8896b3; text-align: center; margin-top: 2px; }

.selected-summary { margin-bottom: 14px; }
.summary-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.summary-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px 4px 12px; background: #eef2fb;
  border: 1px solid #c5d0ee; border-radius: 20px;
  font-size: 11.5px; font-weight: 500; color: #2D4B95;
}
.chip-remove {
  background: none; border: none; padding: 0; cursor: pointer;
  display: flex; align-items: center; opacity: 0.7; transition: opacity 0.15s;
}
.chip-remove:hover { opacity: 1; }

.fade-slide-enter-active, .fade-slide-leave-active { transition: all 0.2s ease; }
.fade-slide-enter, .fade-slide-leave-to { opacity: 0; transform: translateY(-6px); }
</style>
